/**
 * Copyright (c) 2019-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { PluginBehavior } from '../../../behavior.js';
import { ParamDefinition as PD } from '../../../../../mol-util/param-definition.js';
import { InteractionsProvider } from '../../../../../mol-model-props/computed/interactions.js';
import { StateSelection } from '../../../../../mol-state/index.js';
import { PluginStateObject } from '../../../../../mol-plugin-state/objects.js';
import { StructureElement } from '../../../../../mol-model/structure/structure/element.js';
import { OrderedSet } from '../../../../../mol-data/int.js';
import { featureGroupLabel, featureTypeLabel } from '../../../../../mol-model-props/computed/interactions/common.js';
import { arraySetAdd } from '../../../../../mol-util/array.js';
import { InteractionTypeColorThemeProvider } from '../../../../../mol-model-props/computed/themes/interaction-type.js';
import { InteractionsRepresentationProvider } from '../../../../../mol-model-props/computed/representations/interactions.js';
export const Interactions = PluginBehavior.create({
    name: 'computed-interactions-prop',
    category: 'custom-props',
    display: { name: 'Interactions' },
    ctor: class extends PluginBehavior.Handler {
        constructor() {
            super(...arguments);
            this.provider = InteractionsProvider;
            this.labelProvider = {
                label: (loci) => {
                    if (!this.params.showTooltip)
                        return void 0;
                    switch (loci.kind) {
                        case 'element-loci':
                            if (loci.elements.length === 0)
                                return void 0;
                            const labels = [];
                            const structures = this.getStructures(loci.structure);
                            for (const s of structures) {
                                const interactions = this.provider.get(s).value;
                                if (!interactions)
                                    continue;
                                const l = StructureElement.Loci.remap(loci, s);
                                if (l.elements.length !== 1)
                                    continue;
                                const e = l.elements[0];
                                if (OrderedSet.size(e.indices) !== 1)
                                    continue;
                                const features = interactions.unitsFeatures.get(e.unit.id);
                                if (!features)
                                    continue;
                                const typeLabels = [];
                                const groupLabels = [];
                                const label = [];
                                const idx = OrderedSet.start(e.indices);
                                const { types, groups, elementsIndex: { indices, offsets } } = features;
                                for (let i = offsets[idx], il = offsets[idx + 1]; i < il; ++i) {
                                    const f = indices[i];
                                    const type = types[f];
                                    const group = groups[f];
                                    if (type)
                                        typeLabels.push(featureTypeLabel(type));
                                    if (group)
                                        groupLabels.push(featureGroupLabel(group));
                                }
                                if (typeLabels.length)
                                    label.push(`<small>Types</small> ${typeLabels.join(', ')}`);
                                if (groupLabels.length)
                                    label.push(`<small>Groups</small> ${groupLabels.join(', ')}`);
                                if (label.length)
                                    labels.push(`Interaction Feature: ${label.join(' | ')}`);
                            }
                            return labels.length ? labels.join('<br/>') : undefined;
                        default: return void 0;
                    }
                }
            };
        }
        getStructures(structure) {
            const structures = [];
            const root = this.ctx.helpers.substructureParent.get(structure);
            if (root) {
                const state = this.ctx.state.data;
                const selections = state.select(StateSelection.Generators.ofType(PluginStateObject.Molecule.Structure, root.transform.ref));
                for (const s of selections) {
                    if (s.obj)
                        arraySetAdd(structures, s.obj.data);
                }
            }
            return structures;
        }
        update(p) {
            const updated = (this.params.autoAttach !== p.autoAttach ||
                this.params.showTooltip !== p.showTooltip);
            this.params.autoAttach = p.autoAttach;
            this.params.showTooltip = p.showTooltip;
            this.ctx.customStructureProperties.setDefaultAutoAttach(this.provider.descriptor.name, this.params.autoAttach);
            return updated;
        }
        register() {
            this.ctx.customStructureProperties.register(this.provider, this.params.autoAttach);
            this.ctx.representation.structure.themes.colorThemeRegistry.add(InteractionTypeColorThemeProvider);
            this.ctx.managers.lociLabels.addProvider(this.labelProvider);
            this.ctx.representation.structure.registry.add(InteractionsRepresentationProvider);
        }
        unregister() {
            this.ctx.customStructureProperties.unregister(this.provider.descriptor.name);
            this.ctx.representation.structure.themes.colorThemeRegistry.remove(InteractionTypeColorThemeProvider);
            this.ctx.managers.lociLabels.removeProvider(this.labelProvider);
            this.ctx.representation.structure.registry.remove(InteractionsRepresentationProvider);
        }
    },
    params: () => ({
        autoAttach: PD.Boolean(false),
        showTooltip: PD.Boolean(true)
    })
});
